'use client';
import { useAxios } from '@/hooks';
import { setCartData } from '@/redux/actions/user.action';
import { AppDispatch, RootState } from '@/redux/store';
import { EventDataType } from '@/types/EventDataType.type';
import { endPoint } from '@/utils';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export function useEventInfo(slug: string | string[] | undefined) {
  const [step, setStep] = useState(0);
  const onNext = (specificStep?: number) => specificStep ? setStep(specificStep) : setStep(step => step + 1);

  const [eventInfo, setEventInfo] = useState<EventDataType | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const { fetchData, loading } = useAxios();

  const fetchEventInfo = async () => {
    try {
      const result: any = await fetchData({
        url: endPoint.FETCH_EVENT_INFO,
        method: 'GET',
        params: {
          eventCode: slug,
        },
      });

      const res: EventDataType = result?.data;

      if (!res?.isRecurringEvent) {
        // Preserve any tickets/time already in the cart when it's this same
        // event - this fetch reruns on every remount (e.g. navigating away and
        // back to the same event), and used to blindly wipe out selections the
        // user had already made. But if the cart belongs to a DIFFERENT event
        // (the user was previously browsing/buying tickets elsewhere), those
        // leftover tickets must not carry over and inflate this event's cart.
        //
        // Compare against res.eventCode (the canonical code the backend
        // resolved), not the raw `slug` route param - the URL text can vary in
        // casing/encoding/aliasing between visits to the same event, which
        // would make this check spuriously see a "different" event and wipe a
        // perfectly valid cart.
        dispatch((dispatchAction: AppDispatch, getState: () => RootState) => {
          const existingCart = getState()?.user?.cart;
          const isSameEvent = existingCart?.eventId === res.eventCode;
          dispatchAction(
            setCartData(
              { date: res.eventDate ?? res.dateString },
              isSameEvent ? (existingCart?.selectedTime ?? {}) : {},
              isSameEvent ? (existingCart?.selectedTickets ?? {}) : {},
              res.eventCode,
            ),
          );
        });
        setStep(1);
      }

      setEventInfo(result?.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEventInfo();
  }, []);

  return {
    loading,
    eventInfo,
    step,
    onNext,
  };
}
